import { useSchedulerContext } from '@contexts/scheduler';
import { useScheduler } from '@hooks/scheduler';
import { getSegment, getSegmentFromPoint } from './helpers';
import { parseISO } from 'date-fns';
import { Schedule } from '@root/globalTypes';
import { WorkloadBasicForeman } from '@services/workload/calendar/types';

export const useSchedulerDragAndDrop = () => {
  const {
    inventory,
    active: { mode, newValues },
    pointerRef,
    setState,
  } = useSchedulerContext();
  const { updateDates } = useScheduler();

  const handleDragStart = (schedule: Schedule) => {
    setState((prev) => ({
      ...prev,
      active: {
        ...prev.active,
        mode: 'move',
        newValues: {
          ...prev.active.newValues,
          foreman: schedule.foreman,
          startsAt: parseISO(schedule.startsAt),
          endsAt: parseISO(schedule.endsAt),
        },
      },
    }));
  };

  const handleDragEnd = (foremanId: string) => {
    const newForemanId = newValues.foreman?.id ?? '';
    const currentForemanId = foremanId ?? '';

    setState((prev) => {
      return {
        ...prev,
        active: {
          ...prev.active,
          fresh: false,
          foremanChanged: currentForemanId ? newForemanId !== currentForemanId : true,
          mode: 'move',
          foremanId: newForemanId,
          schedule: {
            ...prev.active.schedule,
            foreman: { ...prev.active.schedule.foreman, id: newForemanId },
            startsAt: !inventory
              ? prev.active.newValues.startsAt?.toISOString() || prev.active.schedule.startsAt
              : prev.active.schedule.startsAt,
            endsAt: !inventory
              ? prev.active.newValues.endsAt?.toISOString() || prev.active.schedule.endsAt
              : prev.active.schedule.endsAt,
          },
        },
      };
    });
  };

  const handleDragMove = () => {
    const { x: clientX, y: clientY } = pointerRef.current;

    const targetSegment = getSegment(clientX, clientY);
    const foremanAttr = targetSegment?.getAttribute('data-foreman');
    const foreman = foremanAttr ? (JSON.parse(foremanAttr) as WorkloadBasicForeman) : undefined;
    const dateAttr = targetSegment?.getAttribute('data-date');
    const to = dateAttr ? parseISO(dateAttr) : undefined;

    setState((prev) => ({
      ...prev,
      active: {
        ...prev.active,
        newValues: {
          ...prev.active.newValues,
          ...updateDates(),
          foreman: foreman && !foreman.discardedAt ? foreman : prev.active.newValues?.foreman,
          from: prev.active.newValues.from || to,
          to: to || prev.active.newValues.to,
        },
      },
    }));
  };

  const handlePointer = (active: boolean, type: 'enter' | 'leave', x?: number, y?: number) => {
    if (!active || mode !== 'move') return;
    const foremanAttr = x && y ? getSegment(x, y)?.getAttribute('data-foreman') : undefined;
    const foreman = foremanAttr ? (JSON.parse(foremanAttr) as WorkloadBasicForeman) : undefined;
    setState((prev) => ({
      ...prev,
      active: {
        ...prev.active,
        cursor: type === 'enter' ? 'cursor-move' : 'cursor-auto',
        newValues: {
          ...prev.active.newValues,
          foreman: foreman && !foreman.discardedAt ? foreman : prev.active.newValues?.foreman,
          from:
            !prev.active.newValues.from && x && y
              ? getSegmentFromPoint(x, y)
              : prev.active.newValues.from,
        },
      },
    }));
  };

  return { handleDragStart, handleDragMove, handleDragEnd, handlePointer };
};
